import { useState } from 'react'
import { Alert, Button, Modal, Tag, Typography, Upload, message } from 'antd'
import { DownloadOutlined, InboxOutlined } from '@ant-design/icons'
import * as uiApi from '@/api/uiAutomation'
import type { UIExcelImportResult, UIStepItem } from '@/types'

const { Text, Paragraph } = Typography

interface UIExcelImportModalProps {
  open: boolean
  onClose: () => void
  /** 导入回调：把解析出的步骤草稿追加进 StepEditor */
  onImport: (steps: UIStepItem[], titleSuggestion?: string) => void
}

/**
 * Excel 用例导入（四期 J）
 * - 上传 xlsx：优先按表头识别（步骤/操作/描述 + 输入数据/测试数据…），每行一条步骤
 * - 无表头结构时回退 LLM 拆分（未配置 LLM 则按"每行一步"降级）
 * - 解析出的步骤草稿预览确认后导入 StepEditor
 * - 内置模板下载（用例名称/步骤描述/输入数据 + 填写说明 sheet）
 */
export default function UIExcelImportModal({ open, onClose, onImport }: UIExcelImportModalProps) {
  const [parsing, setParsing] = useState(false)
  const [downloading, setDownloading] = useState(false)
  const [result, setResult] = useState<UIExcelImportResult | null>(null)

  const handleParse = async (file: File) => {
    setParsing(true)
    setResult(null)
    try {
      const r = await uiApi.importExcelSteps(file)
      setResult(r)
      if (!r.steps.length) {
        message.warning(r.warnings[0] || '未解析到有效步骤')
      } else {
        message.success(`解析完成：${r.steps.length} 个步骤草稿`)
      }
    } catch {
      // 错误提示已由请求层统一弹出
    } finally {
      setParsing(false)
    }
    return false // 阻止 antd Upload 自动上传
  }

  const handleDownloadTemplate = async () => {
    setDownloading(true)
    try {
      await uiApi.downloadExcelTemplate()
      message.success('模板已下载，按「用例步骤」sheet 格式填写后上传')
    } catch (err) {
      message.error(err instanceof Error ? err.message : '模板下载失败')
    } finally {
      setDownloading(false)
    }
  }

  const handleImport = () => {
    if (!result?.steps.length) return
    const steps: UIStepItem[] = result.steps.map((d) => ({
      description: d.description || '',
      data: d.data?.value ? { value: d.data.value } : { value: '' },
      is_script: false,
      script: '',
      wait_until: 'load',
      post_delay: Number(d.post_delay) || 1,
    }))
    onImport(steps, result.case_title || undefined)
    message.success(`已导入 ${steps.length} 个步骤草稿，可继续编辑`)
    onClose()
  }

  return (
    <Modal
      title={
        <span>
          Excel 导入步骤 <Tag color="green">xlsx</Tag>
        </span>
      }
      open={open}
      onCancel={onClose}
      footer={null}
      width={720}
      rootClassName="aurora-modal-wrap"
      className="aurora-modal"
      destroyOnHidden
    >
      <Paragraph style={{ fontSize: 13, color: 'var(--au-slate-muted)', marginBottom: 12 }}>
        支持「步骤/操作/描述」与「输入数据/测试数据」表头的结构化用例表；无表头时自动调用 AI 拆分（未配置 AI 则按每行一步降级）。
        解析出的草稿可在步骤编辑器中继续编辑后执行。
      </Paragraph>

      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 8,
          padding: '8px 12px',
          marginBottom: 10,
          borderRadius: 8,
          background: 'var(--au-primary-50)',
          border: '1px solid var(--au-primary-100)',
        }}
      >
        <Text style={{ fontSize: 12, color: 'var(--au-slate)' }}>
          不知道怎么填？下载模板：每行一条步骤，含「用例名称 / 步骤描述 / 输入数据」三列与填写说明
        </Text>
        <Button size="small" type="primary" ghost icon={<DownloadOutlined />} onClick={handleDownloadTemplate} loading={downloading}>
          下载模板
        </Button>
      </div>

      <Upload.Dragger
        accept=".xlsx,.xlsm"
        maxCount={1}
        showUploadList={false}
        beforeUpload={(file) => handleParse(file)}
        disabled={parsing}
      >
        {parsing ? (
          <div style={{ padding: '24px 0' }}>
            <Text style={{ fontSize: 13 }}>解析中，请稍候…</Text>
          </div>
        ) : (
          <>
            <p className="ant-upload-drag-icon">
              <InboxOutlined />
            </p>
            <p className="ant-upload-text" style={{ fontSize: 14 }}>点击或拖拽 xlsx 文件到此区域解析</p>
            <p className="ant-upload-hint" style={{ fontSize: 12 }}>文件上限 5MB；解析仅在服务端进行，不会存储文件内容</p>
          </>
        )}
      </Upload.Dragger>

      {result && (
        <div style={{ marginTop: 14 }}>
          {result.warnings.length > 0 && (
            <Alert type="warning" showIcon style={{ marginBottom: 10 }} message={result.warnings.join('；')} />
          )}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
            <Text strong style={{ fontSize: 13 }}>
              解析出 {result.steps.length} 个步骤草稿
              {result.case_title ? <Text type="secondary" style={{ fontSize: 12, marginLeft: 8 }}>用例标题：{result.case_title}</Text> : null}
            </Text>
            <Button type="primary" size="small" disabled={!result.steps.length} onClick={handleImport}>
              导入到步骤编辑器
            </Button>
          </div>
          <div style={{ maxHeight: 300, overflow: 'auto', border: '1px solid var(--au-border)', borderRadius: 8, padding: '8px 12px' }}>
            {result.steps.map((s, i) => (
              <div key={i} style={{ display: 'flex', gap: 8, padding: '6px 0', borderBottom: '1px dashed var(--au-border)', fontSize: 13 }}>
                <span style={{ color: 'var(--au-primary)', fontWeight: 600, flexShrink: 0 }}>{i + 1}.</span>
                <span>{s.description}</span>
                {s.data?.value ? <Tag style={{ flexShrink: 0 }}>{s.data.value}</Tag> : null}
              </div>
            ))}
            {!result.steps.length && <Text type="secondary" style={{ fontSize: 12 }}>无有效步骤</Text>}
          </div>
        </div>
      )}
    </Modal>
  )
}
